import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AsyncState } from '@/components/async';
import { FilterInput, PillButton } from '@/components/entity';
import { Skeleton } from '@/components/feedback';
import { EntityTable, type EntityTableColumn } from '@/components/entity/EntityTable';
import { Seo } from '@/components/seo';
import { useLeadsList } from '@/hooks/useLeads';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import type { AppUser } from '@/lib/auth';
import { LEAD_CAMPAIGN_MEDIUM_CONTACT, LEAD_CAMPAIGN_SOURCE_CONTACT } from '@/lib/constants/leads';
import { listShowings, type ShowingWithLead } from '@/lib/services/showings';
import type { LeadListFilters, LeadSummary } from '@/lib/types/lead';
import type { ShowingStatus } from '@/lib/types/showing';
import { formatLeadDisplayName } from '@/lib/utils/nameFormatting';
import { formatUnitLabel, humanizeAddressText } from '@/lib/utils/propertyFormatting';
import { formatShowingSlotLabel } from '@/lib/utils/showingsFormatting';

type ScheduledShowingStatus = Extract<ShowingStatus, 'confirmed' | 'rescheduled'>;

interface ScheduledLeadSummary {
  leadId: string;
  fullName: string;
  email: string;
  phone: string | null;
  showings: Array<{
    showingId: string;
    slotLabel: string;
    unitLabel: string;
    scheduledAt: string | null;
  }>;
  nextScheduledAt: string | null;
}

export function DashboardLeads(): JSX.Element {
  const user = useDashboardUser();
  const navigate = useNavigate();

  const dateStart = useMemo(() => formatLocalDate(new Date()), []);
  const columns = useMemo((): EntityTableColumn<ScheduledLeadSummary>[] => {
    return [
      {
        id: 'lead',
        header: 'Lead',
        renderCell: (lead) => (
          <div className="flex flex-col gap-1">
            <Link
              to={`/dashboard/leads/${lead.leadId}`}
              className="font-semibold text-text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30"
            >
              {lead.fullName}
            </Link>
            <span className="text-xs text-text-primary/70">{lead.email}</span>
          </div>
        ),
      },
      {
        id: 'phone',
        header: 'Phone',
        renderCell: (lead) => (
          <span className="text-sm text-text-primary/80">{lead.phone ?? '—'}</span>
        ),
      },
      {
        id: 'next',
        header: 'Next showing',
        renderCell: (lead) => {
          const nextShowing = lead.showings.at(0);
          if (!nextShowing) {
            return <span className="text-sm text-text-primary/60">—</span>;
          }
          return (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-text-primary">
                {nextShowing.slotLabel}
              </span>
              <span className="text-xs text-text-primary/70">{nextShowing.unitLabel}</span>
            </div>
          );
        },
      },
      {
        id: 'count',
        header: 'Upcoming',
        align: 'right',
        renderCell: (lead) => (
          <span className="text-sm font-semibold text-text-primary">{lead.showings.length}</span>
        ),
      },
    ];
  }, []);

  const showingsQuery = useQuery<ShowingWithLead[]>({
    queryKey: ['dashboard-showings', { view: 'leads', dateStart }],
    queryFn: async ({ signal }) => {
      return await listShowings({ user, filters: { dateStart }, signal });
    },
  });

  const scheduledLeads = useMemo(() => {
    const scheduledStatuses = new Set<ScheduledShowingStatus>(['confirmed', 'rescheduled']);
    const leadMap = new Map<string, ScheduledLeadSummary>();

    const entries = showingsQuery.data ?? [];

    for (const entry of entries) {
      const showing = entry.showing;
      const status = showing.status as ShowingStatus;
      if (!scheduledStatuses.has(status as ScheduledShowingStatus)) {
        continue;
      }

      const lead = entry.lead;
      const leadId = lead.id;
      const unitLabel = formatShowingUnitLabel(entry);
      const scheduledAt = typeof showing.scheduled_at === 'string' ? showing.scheduled_at : null;
      const slotLabel = formatShowingSlotLabel({
        scheduledAt,
        preferredDate: showing.preferred_date,
        preferredTime: showing.preferred_time,
      });

      const existing =
        leadMap.get(leadId) ??
        ({
          leadId,
          fullName: formatLeadDisplayName({
            firstName: lead.first_name,
            lastName: lead.last_name,
            fullName: lead.full_name,
          }),
          email: lead.email,
          phone: lead.phone ?? null,
          showings: [],
          nextScheduledAt: null,
        } satisfies ScheduledLeadSummary);

      existing.showings.push({
        showingId: showing.id,
        slotLabel,
        unitLabel,
        scheduledAt,
      });

      leadMap.set(leadId, existing);
    }

    const summaries = Array.from(leadMap.values());

    summaries.forEach((summary) => {
      summary.showings.sort((a, b) => compareOptionalDates(a.scheduledAt, b.scheduledAt));
      summary.nextScheduledAt = summary.showings.at(0)?.scheduledAt ?? null;
    });

    return summaries.sort((a, b) => compareOptionalDates(a.nextScheduledAt, b.nextScheduledAt));
  }, [showingsQuery.data]);

  return (
    <section className="flex flex-col gap-xl py-8">
      <Seo
        title="Leads"
        description="View contacts with scheduled showings for Strashin Developments."
        canonicalPath="/dashboard/leads"
        noIndex
      />

      <header className="flex flex-col gap-xs">
        <h1 className="text-2xl font-semibold text-brand-primary md:text-3xl">Leads</h1>
        <p className="text-sm text-text-primary/70">Contact details and scheduled showings.</p>
      </header>

      <AsyncState
        data={showingsQuery.data ? scheduledLeads : null}
        isLoading={showingsQuery.isLoading || showingsQuery.isFetching}
        error={showingsQuery.error}
        skeleton={<LeadsTableSkeleton />}
      >
        {(result) =>
          result.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-surface-subtle bg-white/60 p-6 text-center text-sm text-text-primary/70">
              No scheduled showings yet. Pending requests appear under Showing Requests.
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-surface-subtle bg-white shadow-sm">
              <EntityTable
                items={result}
                columns={columns}
                getRowKey={(lead) => lead.leadId}
                onRowClick={(lead) => {
                  navigate(`/dashboard/leads/${lead.leadId}`);
                }}
              />
            </div>
          )
        }
      </AsyncState>

      <ContactSubmissionsSection user={user} />
    </section>
  );
}

const CONTACT_SUBMISSIONS_PAGE_SIZE = 25;

function ContactSubmissionsSection({ user }: { user: AppUser }): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const filters = useMemo<LeadListFilters>(() => {
    const trimmedSearch = search.trim();
    return {
      campaignSource: LEAD_CAMPAIGN_SOURCE_CONTACT,
      campaignMedium: LEAD_CAMPAIGN_MEDIUM_CONTACT,
      searchTerm: trimmedSearch.length > 0 ? trimmedSearch : null,
    };
  }, [search]);

  const submissionsQuery = useLeadsList({
    user,
    page,
    pageSize: CONTACT_SUBMISSIONS_PAGE_SIZE,
    filters,
  });

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    const safeTotalPages = Math.max(submissionsQuery.data?.totalPages ?? 1, 1);
    if (page > safeTotalPages) {
      setPage(safeTotalPages);
    }
  }, [page, submissionsQuery.data?.totalPages]);

  const columns = useMemo<ReadonlyArray<EntityTableColumn<LeadSummary>>>(() => {
    return [
      {
        id: 'contact',
        header: 'Contact',
        renderCell: (lead) => (
          <div className="flex flex-col gap-1">
            <Link
              to={`/dashboard/leads/${lead.id}`}
              className="font-semibold text-text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/30"
            >
              {lead.fullName}
            </Link>
            <span className="text-xs text-text-primary/70">{lead.email}</span>
          </div>
        ),
      },
      {
        id: 'phone',
        header: 'Phone',
        renderCell: (lead) => (
          <span className="text-sm text-text-primary/80">{lead.phone ?? '—'}</span>
        ),
      },
      {
        id: 'message',
        header: 'Message',
        renderCell: (lead) => {
          const message = formatContactSubmissionMessage(lead.notes);
          return (
            <span
              className="block max-w-[260px] truncate text-xs text-text-primary/70"
              title={message}
            >
              {message}
            </span>
          );
        },
      },
      {
        id: 'submitted',
        header: 'Submitted',
        align: 'right',
        renderCell: (lead) => (
          <span className="text-xs text-text-primary/70">{formatTimestamp(lead.createdAt)}</span>
        ),
      },
    ];
  }, []);

  return (
    <section className="flex flex-col gap-lg">
      <header className="flex flex-col gap-xs">
        <h2 className="text-xl font-semibold text-brand-primary">Contact form submissions</h2>
        <p className="text-sm text-text-primary/70">
          All messages sent from the public contact page.
        </p>
      </header>

      <FilterInput
        id="contact-submissions-search"
        label="Search submissions"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
        placeholder="Filter by name or email"
        size="sm"
        containerClassName="max-w-md"
      />

      <AsyncState
        data={submissionsQuery.data ?? null}
        isLoading={submissionsQuery.isLoading || submissionsQuery.isFetching}
        error={submissionsQuery.error}
        skeleton={<ContactSubmissionsSkeleton />}
      >
        {(result) =>
          result.items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-surface-subtle bg-white/60 p-6 text-center text-sm text-text-primary/70">
              No contact form submissions yet.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="overflow-hidden rounded-2xl border border-surface-subtle bg-white shadow-sm">
                <EntityTable
                  items={result.items}
                  columns={columns}
                  getRowKey={(lead) => lead.id}
                  onRowClick={(lead) => {
                    navigate(`/dashboard/leads/${lead.id}`);
                  }}
                />
              </div>
              <ContactSubmissionsPagination
                page={result.page}
                pageSize={result.pageSize}
                totalItems={result.totalItems}
                totalPages={result.totalPages}
                onPageChange={(nextPage) => {
                  setPage((current) => {
                    const safeTotalPages = Math.max(result.totalPages, 1);
                    const clamped = Math.min(Math.max(nextPage, 1), safeTotalPages);
                    return clamped === current ? current : clamped;
                  });
                }}
              />
            </div>
          )
        }
      </AsyncState>
    </section>
  );
}

function LeadsTableSkeleton(): JSX.Element {
  return (
    <div className="overflow-hidden rounded-2xl border border-surface-subtle bg-white shadow-sm">
      <div className="flex flex-col gap-3 p-4" aria-label="Loading leads">
        <Skeleton className="h-10 w-full rounded-xl" />
        <Skeleton className="h-10 w-full rounded-xl" />
        <Skeleton className="h-10 w-full rounded-xl" />
      </div>
    </div>
  );
}

function ContactSubmissionsSkeleton(): JSX.Element {
  return (
    <div className="overflow-hidden rounded-2xl border border-surface-subtle bg-white shadow-sm">
      <div className="flex flex-col gap-3 p-4" aria-label="Loading contact submissions">
        <Skeleton className="h-10 w-full rounded-xl" />
        <Skeleton className="h-10 w-full rounded-xl" />
        <Skeleton className="h-10 w-full rounded-xl" />
      </div>
    </div>
  );
}

interface ContactSubmissionsPaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function ContactSubmissionsPagination({
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
}: ContactSubmissionsPaginationProps): JSX.Element {
  const safeTotalPages = Math.max(totalPages, 1);
  const hasResults = totalItems > 0;
  const start = hasResults ? (page - 1) * pageSize + 1 : 0;
  const end = hasResults ? Math.min(page * pageSize, totalItems) : 0;
  const rangeLabel = hasResults ? `${start.toLocaleString()}-${end.toLocaleString()}` : '0';

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-sm text-text-primary shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-text-primary/80">
        {`Showing ${rangeLabel} of ${totalItems.toLocaleString()} submissions`}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <PillButton
          size="sm"
          variant="secondary"
          onClick={() => {
            onPageChange(page - 1);
          }}
          disabled={page <= 1}
        >
          Previous
        </PillButton>
        <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          Page {page} of {safeTotalPages}
        </span>
        <PillButton
          size="sm"
          variant="secondary"
          onClick={() => {
            onPageChange(page + 1);
          }}
          disabled={page >= safeTotalPages}
        >
          Next
        </PillButton>
      </div>
    </div>
  );
}

function formatLocalDate(date: Date): string {
  const year = date.getFullYear().toString();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatShowingUnitLabel(entry: ShowingWithLead): string {
  const unit = entry.unit;
  const unitDisplay = unit ? humanizeAddressText(formatUnitLabel(unit)) : '';
  const propertyCode = unit ? humanizeAddressText(unit.propertyCode) : '';
  const hasUnitMeta = unitDisplay.trim().length > 0 && propertyCode.trim().length > 0;
  return hasUnitMeta
    ? `${unitDisplay} · ${propertyCode}`
    : (entry.showing.unit_id ?? 'Unit not set');
}

function compareOptionalDates(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  const aValue = Date.parse(a);
  const bValue = Date.parse(b);

  if (Number.isNaN(aValue) && Number.isNaN(bValue)) {
    return a.localeCompare(b);
  }
  if (Number.isNaN(aValue)) return 1;
  if (Number.isNaN(bValue)) return -1;

  return aValue - bValue;
}

function formatTimestamp(value: string | null): string {
  if (value === null || value.length === 0) return '—';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(parsed));
}

function formatContactSubmissionMessage(notes: string | null): string {
  if (typeof notes !== 'string') {
    return '—';
  }

  const trimmed = notes.trim();
  if (trimmed.length === 0) {
    return '—';
  }

  const firstLine = trimmed.split(/\r?\n/)[0] ?? '';
  if (firstLine.length <= 80) {
    return firstLine;
  }

  return `${firstLine.slice(0, 77)}...`;
}
